import {
    SCHEMA_AUTHOR_CBC,
    SCHEMA_CONTEXT,
    SCHEMA_TYPES,
    SCHEMA_PUBLISHER,
    SCHEMA_PUBLISHER_ID,
} from "./constants";
import {
    VIDEO_CANONICAL_URL_BASE,
    SYNDICATION_PLAYER_URL_BASE,
} from "../../constants/videos";
import log from "../logger/library";

/**
 * Given a timestamp, returns an ISO 8601 string
 *
 * if the timestamp is null or the created date is invalid,
 * undefined is returned
 */
export const timestampToISOString = (timestamp) => {
    if (timestamp === null) return undefined;
    const _timestamp = Number(timestamp);
    const date = new Date(_timestamp);
    return Number.isSafeInteger(date.valueOf())
        ? date.toISOString()
        : undefined;
};

/** Given an array of nested content objects, returns a string
 *  articleBody expects plain text
 */
const extractTextContent = (parsedBody) => {
    const textArray = parsedBody.reduce((accumulator, node) => {
        if (node.type === "text" && node.content.length > 0) {
            accumulator.push(node.content);
        } else if (node.type === "html" && node.content) {
            accumulator.push(extractTextContent(node.content));
        }
        return accumulator;
    }, []);

    return textArray.join(" ");
};

/**
 * Due to inconsistencies in encoding coming from the db, whether it was created in polopoly (story) or 9 (live story), and whether it is a body
 * field or other fields, certain content might not have encoding.  Currently we know data in the body is encoded, but other data is not (title, deck, etc )
 *
 * Encoding characters described here:
 * https://w3c.github.io/json-ld-syntax/#restrictions-for-contents-of-json-ld-script-elements
 */
export const encodeForJsonLD = (content) => {
    if (!content?.replace) return;
    return content
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
};

/**
 * ImageObject
 *
 * https://developers.google.com/search/docs/appearance/structured-data/image-license-metadata#structured-data-type-definitions
 * https://schema.org/ImageObject
 *
 * Required properties:
 * - contentUrl
 * - creditText
 * - creator (if no creditText)
 *
 */
export const getImageObject = (image, ratio = "ratio16x9") => {
    if (!image.aspects?.[ratio]?.url) return null;

    let creator;
    if (!image.credit) {
        creator = SCHEMA_AUTHOR_CBC;
    }

    return {
        ...SCHEMA_TYPES.ImageObject,
        caption: image.description || undefined,
        creditText: image.credit || undefined,
        contentUrl: image.aspects[ratio].url,
        creator: creator,
        url: image.aspects[ratio].url,
    };
};

/**
 * VideoObject / AudioObject
 *
 * https://developers.google.com/search/docs/appearance/structured-data/video#video-object
 * https://schema.org/VideoObject
 * https://schema.org/AudioObject
 *
 * Required properties:
 * - name
 * - thumbnailUrl
 * - uploadDate
 * - sourceId (we can't play the media without it)
 *
 * TODO: Live Videos (BroadcastEvent)
 *      https://developers.google.com/search/docs/appearance/structured-data/video#broadcast-event
 */
export const getMediaObject = ({
    sourceId,
    publishedAt,
    updatedAt,
    type,
    title,
    description,
    image,
    media,
} = {}) => {
    const imageUrl = image?.url || image?.aspects?.ratio16x9?.url;
    if (!sourceId || !publishedAt || !imageUrl || !title) return null;

    const datePublished = timestampToISOString(publishedAt);
    const duration = Math.round(media?.duration ?? 0);
    const mediaType = type === "audio" ? "AudioObject" : "VideoObject";

    return {
        ...SCHEMA_CONTEXT,
        ...SCHEMA_TYPES[mediaType],
        identifier: sourceId,
        name: title,
        description: description || undefined,
        thumbnailUrl: imageUrl,
        uploadDate: datePublished,
        datePublished: datePublished,
        dateModified: timestampToISOString(updatedAt),
        duration: `PT${duration}S`,
        embedUrl: `${SYNDICATION_PLAYER_URL_BASE}${sourceId}`,
        contentUrl: `${VIDEO_CANONICAL_URL_BASE}${type}/${sourceId}`,
        publisher: SCHEMA_PUBLISHER_ID,
    };
};

/**
 * Author(s)
 *
 * https://developers.google.com/search/docs/appearance/structured-data/article#author-bp
 * https://schema.org/author
 *
 * return an array of author objects or CBC Organization if no author is provided
 */
export const getAuthorObject = (authors) => {
    if (!Array.isArray(authors)) return [SCHEMA_AUTHOR_CBC];

    const authorsArray = authors.reduce((accumulator, currentAuthor) => {
        if (currentAuthor?.name)
            accumulator.push({
                ...SCHEMA_TYPES.Person,
                name: currentAuthor.name,
                url: currentAuthor.url || undefined,
            });
        return accumulator;
    }, []);

    return authorsArray.length ? authorsArray : [SCHEMA_AUTHOR_CBC];
};

/**
 * Given an array of media objects, returns an object with all valid media JsonLD grouped by type
 */
const getMediaObjectsbyType = (associatedContent) => {
    if (!Array.isArray(associatedContent)) return {};

    const results = associatedContent.reduce(
        (accumulator, contentItem) => {
            let getSingleObject;
            const type = contentItem.type?.toLowerCase();
            switch (type) {
                case "image":
                    getSingleObject = getImageObject;
                    break;
                case "audio":
                case "video":
                    getSingleObject = getMediaObject;
                    break;
                default:
                    getSingleObject = () => undefined;
                    break;
            }

            const nestedObject = getSingleObject(contentItem);
            if (nestedObject) accumulator[type].push(nestedObject);

            return accumulator;
        },
        { audio: [], image: [], video: [] }
    );

    const cleanResultsArray = (array) => {
        if (!array.length) return undefined;
        return array.length === 1 ? array.pop() : array;
    };

    return {
        audio: cleanResultsArray(results.audio),
        image: cleanResultsArray(results.image),
        video: cleanResultsArray(results.video),
    };
};

/**
 * NewsArticle / ReportageNewsArticle
 *
 * https://developers.google.com/search/docs/appearance/structured-data/article#article-types
 * https://schema.org/NewsArticle
 * https://schema.org/ReportageNewsArticle
 *
 * How Polopoly decides between NewsArticle and ReportageNewsArticle?
 * Should we include one media only (leadMedia/mainMedia)?
 *
 * Recommended properties:
 * - author (name, url)
 * - datePublished
 * - dateModified
 * - headline
 * - image (url, caption, creditText)
 *
 */
const getNewsArticleObject = ({
    url,
    title,
    body,
    authorList,
    updatedAt,
    publishedAt,
    description,
    // keywords,
    wordCount,
} = {}) => {
    if (!url || !title) return null;

    const associatedContent = body?.associatedContent ?? [];
    const authors = authorList ?? [];

    const reportageNewsArticle = {
        ...SCHEMA_CONTEXT,
        ...SCHEMA_TYPES.NewsArticle,
        "@id": url,
        publisher: SCHEMA_PUBLISHER_ID,
        ...getMediaObjectsbyType(associatedContent),
        author: getAuthorObject(authors),
        dateModified: timestampToISOString(updatedAt),
        datePublished: timestampToISOString(publishedAt),
        description: description,
        headline: encodeForJsonLD(title),
        wordCount: wordCount || undefined,
        // keywords: keywords, // ??
        // alternativeHeadline: "", // ???
    };

    return reportageNewsArticle;
};

/**
 * LiveBlogPosting + BlogPosting
 *
 * https://schema.org/LiveBlogPosting is the type of the parent object
 * https://schema.org/BlogPosting is the type of each item in the liveBlogUpdate array
 *
 * Required properties:
 * - coverageStartTime
 * - coverageEndTime (if ended)
 * - headline
 * - author / publisher
 *
 */
const getLiveBlogPostingObject = ({
    url,
    publishedAt,
    updatedAt,
    title,
    deck,
    flag,
    liveStory,
} = {}) => {
    const coverageStartTime = timestampToISOString(publishedAt);
    if (!url || !coverageStartTime || !title) return null;

    const liveStoryUpdates = liveStory?.updates?.nodes ?? [];
    const liveBlogUpdates = liveStoryUpdates.reduce(
        (accumulator, updateItem) => {
            const associatedContent = updateItem.body?.associatedContent ?? [];
            const bodyParsed = updateItem.body?.parsed ?? [];

            const datePublished = timestampToISOString(updateItem.publishedAt);
            const dateModified = timestampToISOString(updateItem.updatedAt);
            accumulator.push({
                ...SCHEMA_TYPES.BlogPosting,
                articleBody: extractTextContent(bodyParsed),
                dateModified: dateModified ?? datePublished,
                datePublished: datePublished,
                url: `${url}?ts=${updateItem.publishedAt}`,
                headline: encodeForJsonLD(updateItem.title),
                ...getMediaObjectsbyType(associatedContent),
                author: getAuthorObject([updateItem.author]),
            });
            return accumulator;
        },
        []
    );

    let lastUpdatedAt;
    if (flag !== "live") {
        lastUpdatedAt = timestampToISOString(updatedAt);
    }

    const liveBlogPosting = {
        ...SCHEMA_CONTEXT,
        ...SCHEMA_TYPES.LiveBlogPosting,
        publisher: SCHEMA_PUBLISHER_ID,
        name: encodeForJsonLD(title),
        headline: encodeForJsonLD(title),
        description: encodeForJsonLD(deck) ?? undefined,
        mainEntityOfPage: { "@id": url },
        coverageStartTime: timestampToISOString(publishedAt),
        coverageEndTime: lastUpdatedAt,
        liveBlogUpdate: liveBlogUpdates,
    };

    return liveBlogPosting;
};

/**
 * Get the structured data for a live story page
 */
export const getLiveStoryPageJsonLD = (content = {}) => {
    const graph = [SCHEMA_PUBLISHER];

    // Added try/catch block to prevent the live story page from crashing if there is an error
    try {
        const newsArticle = getNewsArticleObject(content);
        if (newsArticle) graph.push(newsArticle);

        const liveBlogPosting = getLiveBlogPostingObject(content);
        if (liveBlogPosting) graph.push(liveBlogPosting);
    } catch (error) {
        log.error(
            "(getLiveStoryPageJsonLD) Error generating JsonLD for live story page",
            error
        );
    }

    return {
        ...SCHEMA_CONTEXT,
        "@graph": graph,
    };
};

/**
 * Get the structured data for a video detail page
 *
 * TODO: Adjust the implementation as VDP data format is different from the live story page
 */

// export const getVideoDetailPageJsonLD = (content = {}) => {
//     const jsonLD = [
//         SCHEMA_PUBLISHER,
//     ];

//     const mediaObject = getMediaObject(content);
//     if (mediaObject)
//         jsonLD.push(mediaObject);

//     return jsonLD;
// };
